<template>
  <v-card
    :class="[{'mx-4': $vuetify.breakpoint.mdAndUp}, {'mx-8': $vuetify.breakpoint.lgAndUp}]"
    style="position: relative"
  >
    <v-overlay
      :value="!!isLoading"
      absolute
      color="white"
    >
      <v-progress-circular
        color="primary"
        indeterminate
      />
    </v-overlay>
    <api-errors v-bind="{errors}" />
    <div
      class="pa-6 pb-4 d-flex justify-space-around font-weight-bold"
      :class="{'flex-column': $vuetify.breakpoint.lgAndDown}"
    >
      <div>{{ `Tables: ${server.tables.length || 0}/${server.maxTables}` }}</div>
      <div>{{ `Covers: ${server.covers}/${server.maxCovers}` }}</div>
      <div>Reservations: {{ reservationCounts }}</div>
    </div>
    <v-expand-transition>
      <simple-expansion-card
        ref="tables"
        :value="false"
        hide-arrow
      >
        <template #title>
          <div
            class="pl-2"
            :style="{'word-break': 'break-word'}"
          >
            {{ $vuetify.breakpoint.mdAndDown ? 'Table Options' : 'Available Tables' }}
          </div>
        </template>
        <template #title-right>
          <lock-timer
            v-bind="getLockTimerSettings(
              [itemData],
              {
                recordType: 'diningServer',
                children: true,
                filter_methods: {filterTablesByDate: [date]},
                include_fields: ['diningServerTables.locationId', 'diningServerTables.locationName'],
                filter: [
                  {field: 'id', operator: 'EQUAL', values: [server.id]},
                  {field: 'diningServerTables.mealPeriodId', operator: 'EQUAL', values: [mealPeriodId]},
                ]
              },
              () => $emit('deselect-server')
            )"
            small-mode-breakpoint="lg"
          />
          <v-btn
            :class="[{'mr-2': $vuetify.breakpoint.lgAndUp}]"
            :icon="$vuetify.breakpoint.mdAndDown"
            :color="$vuetify.breakpoint.mdAndDown ? 'black' : $ftStyle[`pageBarSave${isDirty ? 'Dirty' : ''}ButtonColor`]"
            :light="isDirty"
            @click.stop="save(itemData)"
          >
            <v-icon v-if="$vuetify.breakpoint.mdAndDown">
              mdi-content-save
            </v-icon>
            <span v-else>Save</span>
          </v-btn>
        </template>
        <table-card
          v-for="table in tables"
          :key="`server-table-${table.id}`"
          :server="server"
          :servers="serverAssignmentsMap[table.id]"
          :table="table"
          :selected="!!serverTablesMap[table.id]"
          :allow-add="!serverTablesMap[table.id]"
          :allow-remove="!!serverTablesMap[table.id]"
          @click:add="selectTable(table)"
          @click:remove="selectTable(table)"
        />
      </simple-expansion-card>
    </v-expand-transition>
    <div :style="{height: '10px'}" />
  </v-card>
</template>

<script>
import recordLockHelper from '@/ft-vue-core/mixins/recordLockHelper'
import ApiErrors from '@/ft-vue-core/components/ApiErrors.vue'
import SimpleExpansionCard from '../../helperComponents/SimpleExpansionCard.vue'
import TableCard from '../../helperComponents/TableCard.vue'
export default {
  name: 'ServerCardExpandedContent',
  components: {ApiErrors, SimpleExpansionCard, TableCard},
  mixins: [recordLockHelper],
  props: {
    server: Object,
    serverAssignmentsMap: Object,
    reservationCounts: String,
    locationId: Number,
    mealPeriodId: Number,
    tableConfigId: Number,
    date: [String, Number],
    forEvent: Boolean,
    eventId: Number,
  },
  data () {
    return {
      apiRecordCommand: 'diningServer',
      itemData: {},
      isLoading: 0,
      isDirty: false,
      /**
       * @param {Function} unwatchItemData Call this function to remove
       * watch on `itemData`
       * @see itemData
       */
      unwatchItemData: undefined,
      tables: [],
      allTables: [],
    }
  },
  computed: {
    /**
     * This server's assigned tables mapped by `id` with local index
     */
    serverTablesMap () {
      return this.itemData[this.forEvent ? 'eventTables' : 'tables']?.reduce((acc, r, i) => {
        r._index = i
        acc[r.locationTableId] = r
        return acc
      }, {}) ?? {}
    }
  },
  watch: {
    isLoading (value) {
      this.$emit('is-loading', value)
      if (!value) {
        const ref = this.$refs.tables
        if (ref) setTimeout(() => ref.setIsActive(true), 200) // Use timeout for animation spacing
      }
    }
  },
  mounted () {
    const commands = [
      this.withResourceLockHandler({
        command: this.apiRecordCommand,
        load: r => this.itemData = r?.[0] ?? {},
        afterLoad: r => {
          this.isLoading -= 1
          this.unwatchItemData = this.$watch('itemData', v => this.isDirty = true, {deep: true})
        },
        error: r => this.error?.(r),
        params: {
          children: true,
          filter_methods: this.forEvent ? {} : {filterTablesByDate: [this.date]},
          include_fields: this.forEvent
            ? ['diningServerEventTables.locationId', 'diningServerEventTables.locationName', 'eventTables']
            : ['diningServerTables.locationId', 'diningServerTables.locationName'],
          filter: [
            {field: 'id', operator: 'EQUAL', values: [this.server.id]},
            {field: 'diningServerTables.mealPeriodId', operator: 'EQUAL', values: [this.forEvent ? this.eventId : this.mealPeriodId]},
          ]
        }
      }),
      {
        command: 'diningLocationTable',
        load: this.tables,
        afterLoad: () => this.isLoading -= 1,
        error: r => this.error?.(r),
        params: {
          filter: [
            {field: 'locationId', operator: 'EQUAL', values: [this.locationId]},
            {field: 'disabled', operator: 'EQUAL', values: [false]},
          ],
          filter_methods: {
            filterByShowOnFloorPlan: [true, this.tableConfigId],
            filterByOnTableConfig: [this.tableConfigId]
          }
        }
      },
      {
        command: 'diningLocationTable',
        load: this.allTables,
        afterLoad: () => this.isLoading -= 1,
        error: r => this.error?.(r),
      }
    ]
    this.isLoading = commands.length
    this.$ftAPI().get(commands)
  },
  /**
   * __ALWAYS__ Ensure that any existing record lock is released before
   * this component is destroyed
   */
  beforeDestroy () {
    this.$ftAPI().get({
      command: 'releaseLock',
      error: r => this.error?.(r),
      params: {record_key: this.itemData.recordLockKey},
    })
  },
  methods: {
    /**
     * Add/Remove tables selected on canvas on `itemData`. Final destination for interactions from
     * canvas. Called from parent using $refs
     * @param {Object} table DiningLocationTable Object selected on canvas as server assignment
     */
    selectTable (table) {
      const mappedTable = this.serverTablesMap[table.id]
      const target = this.forEvent ? 'eventTables' : 'tables'
      if (mappedTable) this.itemData[target].splice(mappedTable._index, 1)
      else this.itemData[target].push(this._convertTable(table))
      this.$emit('set-tables', this.itemData[target])
    },
    /**
     * Converts a DiningLocationTable object into a new DiningServerTable object
     * @param {Object} table DiningLocationTable object to be converted to DiningServerTable
     */
    _convertTable (table) {
      const {mealPeriodId, locationId, date, server, eventId} = this
      if (this.forEvent) return {serverId: server.id, locationTableId: table.id, eventId}
      else return {mealPeriodId, locationId, date, serverId: server.id, locationTableId: table.id}
    },
    /**
     * Custom error handler method for internal API requests
     * @param {Object} err Error response object from the API
     */
    error (err = {}) {
      this.isLoading = 0
      this.errors.push(err.message)
    },
    /**
     * Save the current state of `itemData` to `diningServer`
     * @param {Object} itemData Current state/alterations to open DiningServer record.
     * @see itemData
     */
    save (itemData) {
      const {mealPeriodId, eventId, date, error, forEvent} = this
      this.$emit('is-loading', ++this.isLoading)
      this.$ftAPI().put({
        command: forEvent ? 'diningSaveServerTablesByEvent' : 'diningSaveServerTablesByDate',
        afterLoad: () => this.$emit('saved'),
        error,
      }, {
        server: itemData,
        [forEvent ? 'eventId' : 'mealPeriodId']: forEvent ? eventId : mealPeriodId,
        date
      })
    }
  }
}
</script>
